import { recruitmentRoundLabel } from "../services/recruitmentLabels";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  recruitmentAPI,
  RecruitmentRound,
  RoundConfig,
  recruitmentError,
} from "../services/recruitmentAPI";

interface Props {
  value?: RoundConfig;
  onChange: (value: RoundConfig) => void;
  disabled?: boolean;
}
const defaultNames = [
  "Assessment",
  "GD Round",
  "Case Study Round",
  "PI Round 1",
  "PI Round 2",
  "Final Select",
  "Offer Accepted",
  "Joined",
];
export default function RecruitmentRoundEditor({
  value,
  onChange,
  disabled,
}: Props) {
  const [catalog, setCatalog] = useState<RecruitmentRound[]>([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const changeRef = useRef(onChange);
  changeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => {
    let active = true;
    recruitmentAPI
      .rounds()
      .then(({ data }) => {
        if (!active) return;
        setCatalog(data);
        if (!valueRef.current)
          changeRef.current({
            round_ids: defaultNames.map(
              (name) => data.find((r) => r.name === name)!.round_id,
            ),
            cutoff: 70,
            revision: 0,
          });
      })
      .catch((e) => {
        if (active) setError(recruitmentError(e));
      });
    return () => {
      active = false;
    };
  }, []);
  const update = (ids: number[]) =>
    value && onChange({ ...value, round_ids: ids });
  const add = async (renameIndex?: number) => {
    const name =
      renameIndex === undefined
        ? query.trim()
        : window
            .prompt(
              "Enter the round name. Existing matching rounds will be reused.",
              catalog.find((r) => r.round_id === value?.round_ids[renameIndex])
                ?.name,
            )
            ?.trim();
    if (!name || !value) return;
    setAdding(true);
    try {
      const { data } = await recruitmentAPI.addRound(name);
      setCatalog((previous) =>
        previous.some((r) => r.round_id === data.round_id)
          ? previous
          : [...previous, data],
      );
      if (
        value.round_ids.includes(data.round_id) &&
        value.round_ids[renameIndex ?? -1] !== data.round_id
      ) {
        toast.error("That round is already included");
        return;
      }
      update(
        renameIndex === undefined
          ? [...value.round_ids, data.round_id]
          : value.round_ids.map((id, index) =>
              index === renameIndex ? data.round_id : id,
            ),
      );
      setQuery("");
    } catch (e) {
      toast.error(recruitmentError(e));
    } finally {
      setAdding(false);
    }
  };
  if (error)
    return (
      <p role="alert" className="text-red-600">
        {error}
      </p>
    );
  if (!value) return <p role="status">Loading recruitment rounds...</p>;
  const blocked = disabled || adding;
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Round 1 uses the existing assessment link shared by the TPO. Recruiters
        select candidates for the following rounds.
      </p>
      <label className="block text-sm font-medium">
        Assessment cutoff (%)
        <input
          type="number"
          min={0}
          max={100}
          step="0.01"
          disabled={blocked}
          value={value.cutoff}
          onChange={(e) =>
            onChange({ ...value, cutoff: Number(e.target.value) })
          }
          className="ml-3 w-24 rounded border p-2"
        />
      </label>
      <ol className="space-y-2">
        {value.round_ids.map((id, index) => (
          <li
            key={id}
            className="flex flex-wrap items-center gap-2 rounded border bg-gray-50 p-3"
          >
            <span className="w-20 text-sm text-gray-500">
              Round {index + 1}
            </span>
            <strong className="mr-auto">
              {recruitmentRoundLabel(catalog.find((r) => r.round_id === id)?.name || `Round ${id}`)}
            </strong>
            {index === 0 ? (
              <span className="text-xs text-gray-500">
                Existing online test · fixed
              </span>
            ) : (
              <>
                <button
                  type="button"
                  disabled={blocked || index === 1}
                  aria-label={`Move round ${index + 1} up`}
                  onClick={() => {
                    const ids = [...value.round_ids];
                    [ids[index - 1], ids[index]] = [ids[index], ids[index - 1]];
                    update(ids);
                  }}
                  className="rounded border px-2 disabled:opacity-30"
                >
                  ↑
                </button>
                <button
                  type="button"
                  disabled={blocked || index === value.round_ids.length - 1}
                  aria-label={`Move round ${index + 1} down`}
                  onClick={() => {
                    const ids = [...value.round_ids];
                    [ids[index + 1], ids[index]] = [ids[index], ids[index + 1]];
                    update(ids);
                  }}
                  className="rounded border px-2 disabled:opacity-30"
                >
                  ↓
                </button>
                <button
                  type="button"
                  disabled={blocked}
                  onClick={() => void add(index)}
                  className="px-2 text-blue-600"
                >
                  Rename
                </button>
                <button
                  type="button"
                  disabled={blocked}
                  onClick={() =>
                    update(value.round_ids.filter((r) => r !== id))
                  }
                  className="px-2 text-red-600"
                >
                  Remove
                </button>
              </>
            )}
          </li>
        ))}
      </ol>
      <div className="rounded border p-3">
        <label className="block text-sm font-medium">
          Search or add a round
          <input
            value={query}
            disabled={blocked}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search GD, case study, interview..."
            className="mt-2 w-full rounded border p-2"
          />
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          {catalog
            .filter(
              (r) =>
                r.kind !== "assessment" &&
                !value.round_ids.includes(r.round_id) &&
                `${r.name} ${recruitmentRoundLabel(r.name)}`.toLowerCase().includes(query.toLowerCase()),
            )
            .slice(0, 12)
            .map((r) => (
              <button
                key={r.round_id}
                type="button"
                disabled={blocked || value.round_ids.length >= 30}
                onClick={() => {
                  update([...value.round_ids, r.round_id]);
                  setQuery("");
                }}
                className="rounded bg-blue-50 px-3 py-2 text-sm text-blue-700"
              >
                + {recruitmentRoundLabel(r.name)}
              </button>
            ))}
        </div>
        {query.trim() && (
          <button
            type="button"
            disabled={blocked || value.round_ids.length >= 30}
            onClick={() => void add()}
            className="mt-3 text-sm text-blue-600"
          >
            Add or reuse “{query.trim()}”
          </button>
        )}
      </div>
    </div>
  );
}
